import { z } from 'zod';
import { commissioningThresholdAdmissionDecisionSchema, commissioningThresholdValiditySchema } from './commissioning-threshold-economics';
import { commissioningThresholdMeasureSchema } from './commissioning-threshold-measures';
import { commissioningEconomicOwnershipSchema } from './commissioning-competition-context';
import { commissioningNumericObservationSnapshotSchema } from './commissioning-numeric-observations';

const uuid = z.string().uuid();
const revision = z.string().regex(/^[a-f0-9]{64}$/);
export const commissioningThresholdCursorSchema = z.object({
  occurredAt: z.string().datetime({ offset: true }), eventId: uuid,
}).strict();
export type CommissioningThresholdCursor = z.infer<typeof commissioningThresholdCursorSchema>;

export const commissioningThresholdSubjectSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('store'), id: uuid }).strict(),
  z.object({ kind: z.literal('company'), id: uuid }).strict(),
  z.object({ kind: z.literal('user'), id: uuid }).strict(),
  z.object({ kind: z.literal('economic_subject'), id: uuid }).strict(),
]);
export const commissioningThresholdAxisSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('driver'), driverId: uuid }).strict(),
  z.object({ kind: z.literal('overall') }).strict(),
]);

/** A recalculation belongs to a real assignment/member and one competence
 * period. Subject identifiers and date ranges are resolved from that frozen
 * recipe, never accepted as substitutes for the membership. */
export const commissioningThresholdPreviousMembershipSchema = z.object({
  assignmentId: uuid,
  memberId: uuid,
  ruleVersion: z.number().int().positive(),
}).strict();
export type CommissioningThresholdPreviousMembership =
  z.infer<typeof commissioningThresholdPreviousMembershipSchema>;

export const commissioningThresholdRecalculationScopeSchema = z.object({
  competitionId: uuid,
  instanceId: uuid,
  assignmentId: uuid,
  memberId: uuid,
  periodId: uuid,
  axis: commissioningThresholdAxisSchema,
  ruleVersion: z.number().int().positive(),
  /** Server-owned provenance, not a second commercial assignment. It lets one
   * atomic replay reconcile the old and new membership of the same subject. */
  previousMemberships: z.array(commissioningThresholdPreviousMembershipSchema).min(1).max(5000).optional(),
  /** Remove only this historical subject's effects; never evaluate a missing
   * current assignment as though it were still active. */
  retire: z.literal(true).optional(),
}).strict().superRefine((scope, ctx) => {
  const seen = new Set<string>();
  for (const [index, previous] of (scope.previousMemberships ?? []).entries()) {
    const key = `${previous.assignmentId}:${previous.memberId}`;
    if (seen.has(key) || previous.ruleVersion >= scope.ruleVersion) ctx.addIssue({
      code: z.ZodIssueCode.custom, path: ['previousMemberships', index],
      message: 'La provenienza deve essere univoca e precedere la nuova pubblicazione',
    });
    seen.add(key);
  }
  if (scope.retire && !scope.previousMemberships?.some((previous) =>
    previous.assignmentId === scope.assignmentId && previous.memberId === scope.memberId)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['previousMemberships'],
      message: 'Il ritiro richiede la fotografia precedente della membership interessata' });
  }
});
export type CommissioningThresholdRecalculationScope =
  z.infer<typeof commissioningThresholdRecalculationScopeSchema>;

/** Server-owned intent. Persisted in the existing recalculation registry in
 * the same transaction as the approved configuration change. */
export const commissioningThresholdRunContextSchema = z.object({
  schemaVersion: z.literal(1),
  scope: commissioningThresholdRecalculationScopeSchema,
  reason: z.string().trim().min(1).max(500),
}).strict();
export type CommissioningThresholdRunContext = z.infer<typeof commissioningThresholdRunContextSchema>;

export const commissioningRecalculationRunStatusSchema = z.enum([
  'pending', 'running', 'completed', 'partial', 'blocked', 'cancelled', 'snapshot_conflict',
]);
export type CommissioningRecalculationRunStatus = z.infer<typeof commissioningRecalculationRunStatusSchema>;

export const commissioningThresholdRunReportSchema = z.object({
  schemaVersion: z.literal(1),
  message: z.string(),
  replacementRunId: uuid.optional(),
  validity: commissioningThresholdValiditySchema.optional(),
  result: z.object({
    eventsTotal: z.number().int().nonnegative(),
    eventsChanged: z.number().int().nonnegative(),
    deltaAmountEur: z.number().finite(),
    hasLockedPeriods: z.boolean(),
    hasProvisionalResults: z.boolean(),
  }).strict().optional(),
}).strict();
export type CommissioningThresholdRunReport = z.infer<typeof commissioningThresholdRunReportSchema>;

export const commissioningThresholdRunRecordSchema = z.object({
  id: uuid,
  context: commissioningThresholdRunContextSchema,
  status: commissioningRecalculationRunStatusSchema,
  report: commissioningThresholdRunReportSchema.nullable(),
  createdAt: z.string().datetime({ offset: true }),
  completedAt: z.string().datetime({ offset: true }).nullable(),
}).strict();
export type CommissioningThresholdRunRecord = z.infer<typeof commissioningThresholdRunRecordSchema>;

/** Latest request for each subject/period/axis, not economic totals or history
 * counts. Only diagnostic message groups are sampled; counts are complete. */
export const commissioningThresholdRunSummarySchema = z.object({
  schemaVersion: z.literal(1),
  counts: z.array(z.object({
    status: commissioningRecalculationRunStatusSchema,
    count: z.number().int().positive(),
  }).strict()),
  issues: z.array(z.object({
    status: commissioningRecalculationRunStatusSchema,
    message: z.string().min(1),
    count: z.number().int().positive(),
  }).strict()).max(5),
  otherIssueGroups: z.number().int().nonnegative(),
  observedAt: z.string().datetime({ offset: true }),
}).strict();
export type CommissioningThresholdRunSummary = z.infer<typeof commissioningThresholdRunSummarySchema>;

export const commissioningThresholdRunJobSchema = z.object({
  type: z.literal('recalculate-threshold-scope'),
  tenantId: uuid,
  runId: uuid,
}).strict();
export type CommissioningThresholdRunJob = z.infer<typeof commissioningThresholdRunJobSchema>;

/** One whole-competition recalculation intent, recorded in the same transaction
 * as a confirmed context revision whose published recipes cannot be replayed per
 * subject. It is the single plan of that revision: no per-subject runs beside it. */
export const commissioningCompetitionTotalRunContextSchema = z.object({
  schemaVersion: z.literal(1),
  competitionId: uuid,
  reason: z.string().trim().min(1).max(500),
}).strict();
export type CommissioningCompetitionTotalRunContext = z.infer<typeof commissioningCompetitionTotalRunContextSchema>;
export const commissioningCompetitionTotalRunRecordSchema = z.object({
  id: uuid,
  context: commissioningCompetitionTotalRunContextSchema,
  status: commissioningRecalculationRunStatusSchema,
  report: commissioningThresholdRunReportSchema.nullable(),
  createdAt: z.string().datetime({ offset: true }),
  completedAt: z.string().datetime({ offset: true }).nullable(),
}).strict();
export type CommissioningCompetitionTotalRunRecord = z.infer<typeof commissioningCompetitionTotalRunRecordSchema>;

/** A reusable catalog variable is bound here, in the competition, not in its template. */
export const commissioningThresholdSourceBindingSchema = z.object({
  instanceId: uuid,
  axis: commissioningThresholdAxisSchema,
  subjectMapping: z.enum(['same_store', 'same_company', 'same_user', 'store_company', 'same_economic_subject']),
}).strict();
export type CommissioningThresholdSourceBinding = z.infer<typeof commissioningThresholdSourceBindingSchema>;
export const commissioningThresholdSourceBindingsSchema = z.record(
  z.string().regex(/^[a-z][a-z0-9_]*$/), commissioningThresholdSourceBindingSchema,
);
export type CommissioningThresholdSubject = z.infer<typeof commissioningThresholdSubjectSchema>;

export const commissioningThresholdSourceObservationSchema = z.object({
  variableCode: z.string(),
  variableVersion: z.number().int().positive(),
  instanceId: uuid,
  competitionId: uuid,
  memberId: uuid.nullable(),
  subject: commissioningThresholdSubjectSchema.nullable(),
  economicOwnership: commissioningEconomicOwnershipSchema.optional(),
  axis: commissioningThresholdAxisSchema,
  periodId: uuid.nullable(),
  window: z.object({ start: z.string(), end: z.string() }).strict(),
  observation: z.enum(['persisted', 'same_run']),
  stateId: uuid.nullable(),
  revision: revision.nullable(),
  ruleVersion: z.number().int().nonnegative().nullable(),
  observedAt: z.string().datetime({ offset: true }),
  /** Exact inclusive prefix; omitted means the declared complete source window. */
  cutoff: commissioningThresholdCursorSchema.optional(),
  status: z.enum(['ready', 'pending', 'stale', 'not_evaluable']),
  naturalBandId: uuid.nullable(),
  effectiveBandId: uuid.nullable(),
  validity: commissioningThresholdValiditySchema,
}).strict().superRefine((value, ctx) => {
  if (value.economicOwnership && (value.subject?.kind !== 'economic_subject'
    || value.subject.id !== value.economicOwnership.economic_subject_id)
    || value.status === 'ready' && value.subject?.kind === 'economic_subject' && !value.economicOwnership) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['economicOwnership'],
      message: 'La sorgente economica pronta richiede proprietà e revisione coerenti' });
  }
  if (value.status === 'ready' && (value.validity.status !== 'valid' || !value.revision
    || !value.memberId || !value.subject || !value.periodId || value.ruleVersion === null
    || (value.observation === 'persisted' && !value.stateId))) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Una sorgente pronta richiede identità, periodo e revisione verificati' });
  }
  if (value.status !== 'ready' && value.validity.status === 'valid') ctx.addIssue({
    code: z.ZodIssueCode.custom, path: ['validity'], message: 'Una sorgente pendente o obsoleta non è una misura valida',
  });
});
export type CommissioningThresholdSourceObservation = z.infer<typeof commissioningThresholdSourceObservationSchema>;

export const commissioningThresholdEvaluationContextSchema = z.object({
  schemaVersion: z.literal(1),
  ruleVersion: z.number().int().nonnegative(),
  observedAt: z.string().datetime({ offset: true }),
  sources: z.array(commissioningThresholdSourceObservationSchema),
  numericObservations: commissioningNumericObservationSnapshotSchema.optional(),
  /** The same function can also be a condition; this is its paid CONTA outcome. */
  counting: z.object({
    functionId: uuid, outcome: z.enum(['match', 'no_match', 'not_evaluable']),
  }).strict().optional(),
  failures: z.record(commissioningThresholdValiditySchema).refine(
    (values) => Object.values(values).every((value) => value.status !== 'valid'),
    'Una lettura fallita non può essere una misura valida',
  ).optional(),
}).strict();
export type CommissioningThresholdEvaluationContext = z.infer<typeof commissioningThresholdEvaluationContextSchema>;

/** Informational state only. It is deliberately NOT a CommissioningAggregateResult. */
export const commissioningThresholdStateSchema = z.object({
  kind: z.literal('threshold_state'), schemaVersion: z.literal(1),
  tenantId: uuid, competitionId: uuid, instanceId: uuid, periodId: uuid, memberId: uuid,
  subject: commissioningThresholdSubjectSchema,
  economicOwnership: commissioningEconomicOwnershipSchema.optional(),
  axis: commissioningThresholdAxisSchema,
  ruleVersion: z.number().int().nonnegative(),
  configurationRevision: revision,
  factsRevision: revision,
  revision,
  observedAt: z.string().datetime({ offset: true }),
  cursor: commissioningThresholdCursorSchema.nullable(),
  cutoff: commissioningThresholdCursorSchema.optional(),
  status: z.enum(['ready', 'pending', 'stale', 'not_evaluable']),
  measure: commissioningThresholdMeasureSchema,
  admission: commissioningThresholdAdmissionDecisionSchema,
  sources: z.array(commissioningThresholdSourceObservationSchema),
  numericObservations: commissioningNumericObservationSnapshotSchema.optional(),
  /** Historical proof for a terminal unavailable state, never an award. */
  retirement: commissioningThresholdPreviousMembershipSchema.optional(),
}).strict().superRefine((value, ctx) => {
  if (value.subject.kind === 'economic_subject'
    ? value.economicOwnership?.economic_subject_id !== value.subject.id
    : value.economicOwnership !== undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['economicOwnership'],
      message: 'Lo stato del soggetto economico richiede la propria attribuzione fotografata' });
  }
  if (value.status === 'ready' && (value.measure.validity.status !== 'valid' || value.admission.validity.status !== 'valid')) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Una banda pronta richiede misura e ammissione valide' });
  }
  if (value.retirement && (value.retirement.memberId !== value.memberId
    || value.retirement.ruleVersion >= value.ruleVersion || value.status !== 'not_evaluable'
    || value.measure.value !== null || value.measure.history !== 'incomplete'
    || value.measure.validity.code !== 'source_scope_invalid' || value.admission.validity.code !== 'source_scope_invalid'
    || value.admission.naturalBandId !== null || value.admission.effectiveBandId !== null
    || value.admission.allowedFlatBandIds.length > 0 || value.sources.length > 0 || value.cutoff)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['retirement'],
      message: 'Il ritiro richiede una fotografia precedente e uno stato non valutabile senza premi' });
  }
});
export type CommissioningThresholdState = z.infer<typeof commissioningThresholdStateSchema>;

export const commissioningThresholdStateRecordSchema = z.object({
  id: uuid,
  isCurrent: z.boolean(),
  supersededBy: uuid.nullable(),
  state: commissioningThresholdStateSchema,
}).strict();
export type CommissioningThresholdStateRecord = z.infer<typeof commissioningThresholdStateRecordSchema>;
